
import * as THREE from 'three';
import { modeller } from "./Modeller.js";

const phi = Math.sqrt(5) * 0.5 + 0.5;
const phi_inv = Math.sqrt(5) * 0.5 - 0.5;


// 多种形态构造器
export class VoroStandMaker {
    stickRadius = 1; // 连接圆棍的半径
    nParticles = 100; // 随机点的数量
    scale = 30;
    modelDirtyFlag = false;
    modelToExport = null;
    shapeIndex =  0;
    shapeList = [{
        name: '正四面体',
        url: 'makeVoroCube/randomVoroShape',
        type: 'tetrahedron2',
        vertexList: [
            new THREE.Vector3(1, 1, -1),
            new THREE.Vector3(-1, -1, -1),
            new THREE.Vector3(1, -1, 1),
            new THREE.Vector3(-1, 1, 1),
        ],
        volumeRatio: 8 / 3 / Math.pow(2, 3), // 体积所占空间比例
        scale: 1.0
    }, {
        name: '立方体',
        url: 'makeVoroCube/randomVoroShape',
        type: 'cube',
        vertexList: [
            new THREE.Vector3(1, 1, 1),
            new THREE.Vector3(-1, 1, 1),
            new THREE.Vector3(1, -1, 1),
            new THREE.Vector3(-1, -1, 1),
            new THREE.Vector3(1, 1, -1),
            new THREE.Vector3(-1, 1, -1),
            new THREE.Vector3(1, -1, -1),
            new THREE.Vector3(-1, -1, -1),
        ],
        volumeRatio: 1,
        scale: 1.0
    }, {
        name: '正八面体',
        url: 'makeVoroCube/randomVoroShape',
        type: 'octahedron',
        vertexList: [
            new THREE.Vector3(1, 0, 0),
            new THREE.Vector3(-1, 0, 0),
            new THREE.Vector3(0, 1, 0),
            new THREE.Vector3(0, -1, 0),
            new THREE.Vector3(0, 0, 1),
            new THREE.Vector3(0, 0, -1),
        ],
        volumeRatio: 4 / 3 / Math.pow(2, 3),
        scale: 1.0
    }, {
        name: '正十二面体',
        url: 'makeVoroCube/randomVoroShape',
        type: 'dodecahedron',
        vertexList: [
            new THREE.Vector3(1, 1, 1),
            new THREE.Vector3(-1, 1, 1),
            new THREE.Vector3(1, -1, 1),
            new THREE.Vector3(-1, -1, 1),
            new THREE.Vector3(1, 1, -1),
            new THREE.Vector3(-1, 1, -1),
            new THREE.Vector3(1, -1, -1),
            new THREE.Vector3(-1, -1, -1),
            new THREE.Vector3(0, phi, phi_inv),
            new THREE.Vector3(0, -phi, phi_inv),
            new THREE.Vector3(0, phi, -phi_inv),
            new THREE.Vector3(0, -phi, -phi_inv),
            new THREE.Vector3(phi_inv, 0, phi),
            new THREE.Vector3(phi_inv, 0, -phi),
            new THREE.Vector3(-phi_inv, 0, phi),
            new THREE.Vector3(-phi_inv, 0, -phi),
            new THREE.Vector3(phi, phi_inv, 0),
            new THREE.Vector3(-phi, phi_inv, 0),
            new THREE.Vector3(phi, -phi_inv, 0),
            new THREE.Vector3(-phi, -phi_inv, 0),
        ],
        volumeRatio: 4 / 3 / Math.pow(2, 3),
        scale: phi_inv,
    }, {
        name: '正二十面体',
        url: 'makeVoroCube/randomVoroShape',
        type: 'icosahedron',
        vertexList: [
            new THREE.Vector3(-1, phi, 0),
            new THREE.Vector3(1, phi, 0),
            new THREE.Vector3(-1, -phi, 0),
            new THREE.Vector3(1, -phi, 0),
            new THREE.Vector3(0, -1, phi),
            new THREE.Vector3(0, 1, phi),
            new THREE.Vector3(0, -1, -phi),
            new THREE.Vector3(0, 1, -phi),
            new THREE.Vector3(phi, 0, -1),
            new THREE.Vector3(phi, 0, 1),
            new THREE.Vector3(-phi, 0, -1),
            new THREE.Vector3(-phi, 0, 1)
        ],
        volumeRatio: 1,
        scale: 2 / Math.sqrt(10 + 2 * Math.sqrt(5)),
    }];

    makeMesh(scene) {
        this.geom = new THREE.CylinderBufferGeometry(1, 1, 1, 32);
        this.material = new THREE.MeshPhongMaterial({
            color: 0x7E7E7E,
        });
        this.group = new THREE.Group();
        this.group.frustumCulled = false;

        let ball = new THREE.IcosahedronBufferGeometry(1, 4);
        this.mesh2 = new THREE.InstancedMesh(ball, this.material, 20);
        scene.add(this.mesh2);
        scene.add(this.group);
    }

    // 例子位置改变时调用
    makeModelForDisplay() {
        let data = this.particles;

        let count = data.indices.length / 2;
        if (this.mesh) {
            this.group.remove(this.mesh);
        }
        this.mesh = new THREE.InstancedMesh(this.geom, this.material, count);
        this.mesh.frustumCulled = false;
        this.group.add(this.mesh);

        this.updateSizeOrStickRadius();
        this.modelDirtyFlag = true;
    }

    // 尺寸改变时调用
    // 棍子半径改变时调用
    updateSizeOrStickRadius() {
        let data = this.particles;
        let particles = this.particlePositions;

        const r = this.stickRadius / this.scale;
        for (let i = 0; i < data.indices.length; i += 2) {
            let aIndex = data.indices[i];
            let bIndex = data.indices[i + 1];
            let a = particles[aIndex];
            let b = particles[bIndex];
            let ab = new THREE.Vector3().subVectors(b, a);
            let abLen = ab.length();
            // 缩放
            let scale_m = new THREE.Matrix4().makeScale(r, abLen, r);
            // 平移
            let t_m = new THREE.Matrix4().makeTranslation((a.x + b.x) * 0.5, (a.y + b.y) * 0.5, (a.z + b.z) * 0.5);
            // 旋转
            ab.divideScalar(abLen);
            let r_m;
            if (Math.abs(ab.y) > (1.0 - 1e-6)) {
                r_m = new THREE.Matrix4();
            } else {
                // ny -> ab
                let ny = new THREE.Vector3(0, 1, 0);
                let N = new THREE.Vector3().crossVectors(ny, ab).normalize();
                r_m = new THREE.Matrix4().makeRotationAxis(N, Math.acos(ab.y)); // ab.y = ab.x * 0 + ab.y * 1 + ab.z * 0
            }
            let m = new THREE.Matrix4();
            m.multiply(t_m).multiply(r_m).multiply(scale_m);
            this.mesh.setMatrixAt(i / 2, m);
        }
        this.mesh.instanceMatrix.needsUpdate = true;
        this.group.scale.setScalar(this.scale);

        let scaleSize2 = this.scale;
        let vertexList = this.shapeList[this.shapeIndex].vertexList;
        this.mesh2.count = vertexList.length;
        const scale = this.shapeList[this.shapeIndex].scale;
        for (let i = 0; i < vertexList.length; i++) {
            let { x, y, z } = vertexList[i];
            let m1 = new THREE.Matrix4().makeTranslation(x * scale, y * scale, z * scale);
            let m2 = new THREE.Matrix4().makeScale(r, r, r);
            let m3 = new THREE.Matrix4().makeScale(scaleSize2, scaleSize2, scaleSize2);
            let m = new THREE.Matrix4();
            m.multiply(m3).multiply(m1).multiply(m2);
            this.mesh2.setMatrixAt(i, m);
        }
        this.mesh2.instanceMatrix.needsUpdate = true;
        this.modelDirtyFlag = true;
    }

    makeModel() {
        if (!this.modelDirtyFlag) {
            return this.modelToExport;
        }

        if (this.modelToExport) {
            this.modelToExport.geometry.dispose();
            this.modelToExport.material.dispose();
            this.modelToExport = null;
        }

        let vertices = [];
        const r = this.stickRadius;

        let ballGeom = new THREE.IcosahedronBufferGeometry(r, 4);
        this.shapeList[this.shapeIndex].vertexList.forEach(p => {
            let p2 = p.clone();
            p2.multiplyScalar(this.scale);
            modeller.addGeometry(vertices, ballGeom, p2);
        });

        let data = this.particles;
        let particles = [];
        for (let i = 0; i < data.vertices.length; i += 3) {
            let v = new THREE.Vector3(data.vertices[i], data.vertices[i + 1], data.vertices[i + 2]);
            v.multiplyScalar(this.scale);
            particles.push(v);
        }
        for (let i = 0; i < data.indices.length; i += 2) {
            let aIndex = data.indices[i];
            let bIndex = data.indices[i + 1];
            modeller.addStick(vertices, particles[aIndex], particles[bIndex], r, 64);
        }

        let geom = new THREE.BufferGeometry();
        geom.setAttribute("position", new THREE.BufferAttribute(new Float32Array(vertices), 3));

        this.modelToExport = new THREE.Mesh(geom, this.material);
        return this.modelToExport;
    }
}


// WEBPACK FOOTER //
// ./src/views/makeVoroStand/scripts/VoroStandMaker.js